import React from 'react';
import styled from 'styled-components';
import { colors } from '@/lib/colors';
import { CaptionBold } from '@/lib/font';

export type BadgeVariant = 'default' | 'outline' | 'destructive' | 'secondary' | 'new'; // Added 'new' based on design

interface BadgeProps {
    children: React.ReactNode;
    variant?: BadgeVariant;
    className?: string; // For additional tailwind classes if needed
}

// Using styled-components as per codebase pattern, though Tailwind is also an option.
// Given the mix, I'll use styled-components for consistency with other UI parts I've seen or Tailwind if preferred.
// The user rules mention TailwindCSS (utility), so I will use Tailwind classes where possible, or style prop.
// Actually, looking at `PrimaryButton.tsx`, they use styled-components.
// But user said "TailwindCSS (utility) ... Stylesheet -> TailwindCSS".
// I will use Tailwind CSS classes for the implementation to strictly follow "User Rules".

const BadgeContainer = ({ children, variant = 'default', className }: BadgeProps) => {
    let bgClasses = 'bg-primary text-white'; // default
    let borderClasses = '';
    let textClasses = 'text-white';

    switch (variant) {
        case 'outline':
            bgClasses = 'bg-transparent';
            borderClasses = 'border border-gray-300';
            textClasses = 'text-gray-700';
            break;
        case 'secondary':
            bgClasses = 'bg-gray-100';
            textClasses = 'text-gray-800';
            break;
        case 'destructive':
            bgClasses = 'bg-red-500';
            textClasses = 'text-white';
            break;
        case 'new':
            bgClasses = 'bg-[#E8F4FD]'; // Light blue from screenshot inference
            textClasses = 'text-[#427BE8]'; // Blue text
            break;
        default:
            // Primary (Black/Dark Grey in screenshot for "전체" maybe? No, "전체" looks like text.
            // "예약 등록" is a dark badge.
            bgClasses = 'bg-[#3D4244]';
            textClasses = 'text-white';
            break;
    }

    return (
        <span
            className={`
                inline-flex items-center rounded-full px-[13px] py-[5px] text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                ${bgClasses} ${borderClasses} ${textClasses} ${className}
            `}
        >
            {children}
        </span>
    );
};

export default BadgeContainer;
